/**
 * @file string-utils.ts
 * @description String helpers: PascalCase conversion and GUID generation.
 */

/** Converts snake_case or kebab-case to PascalCase (e.g. hero_banner → HeroBanner). */
export const toPascalCase = (str: string): string => {
  return str
    ?.replace(/([a-z])([A-Z])/g, '$1 $2')
    ?.replace(/[_\-\s]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ''))
    ?.replace(/^[a-z]/, (c) => c.toUpperCase());
};

/** Returns a new UUID v4 (crypto.randomUUID). */
export const generateGUID = (): string => {
  return crypto.randomUUID();
};

/** Named HTML entities the CMS and scraped OG metadata commonly escape. */
const NAMED_HTML_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  lsquo: '‘',
  rsquo: '’',
  ldquo: '“',
  rdquo: '”',
  ndash: '–',
  mdash: '—',
  hellip: '…',
};

/** Escapes the five HTML-significant characters so plain text is safe to inject as markup. */
export const escapeHtml = (value?: string | null): string => {
  if (!value) return '';
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

/** Decodes HTML entities (named, decimal and hex) in plain-text strings, e.g. SRP&#039;s → SRP's. */
export const decodeHtmlEntities = (value?: string | null): string => {
  if (!value) return '';
  // Single pass so an escaped entity like &amp;#39; decodes to the literal text &#39; and not an apostrophe
  return value.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z][a-z0-9]*);/gi, (match, entity: string) => {
    if (entity.startsWith('#')) {
      const codePoint =
        entity[1].toLowerCase() === 'x'
          ? Number.parseInt(entity.slice(2), 16)
          : Number.parseInt(entity.slice(1), 10);
      if (!Number.isFinite(codePoint) || codePoint <= 0 || codePoint > 0x10ffff) return match;
      return String.fromCodePoint(codePoint);
    }
    return NAMED_HTML_ENTITIES[entity.toLowerCase()] ?? match;
  });
};
